import { FormDatePicker } from '@/src/components/Form/FormDatePicker'
import { FormNumericInput } from '@/src/components/Form/FormNumericInput'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { appColors } from '@/src/theme'
import { Box, FormLabel } from '@chakra-ui/react'
import { FlexCol } from '@components'
import { addDays, eachDayOfInterval, subDays } from 'date-fns'
import type { FormikProps } from 'formik'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { mapToEnergyTypeDto } from '../../details/types/adjustment.utils'
import { useLoadPlanningEarliestConsumption, useLoadedStrategyMetadata } from '../hooks/LoadPlanning.hooks'
import type { LoadPlanningAdjustmentFormEmptyable } from './LoadPlanningAdjustment.types'
import { getReferencePeriodPickerConfigFromEarliestConsumption } from './ReferencePeriodAdjustment.utils'

interface ReferencePeriodAdjustmentForm {
  formik: FormikProps<LoadPlanningAdjustmentFormEmptyable>
}

const enforceAtLeast7DaysPicked = (dateString: string): Date[] => {
  const date = new Date(dateString)
  const weekBefore = subDays(date, 5)
  const weekAfter = addDays(date, 5)
  return eachDayOfInterval({ start: weekBefore, end: weekAfter })
}

export const ReferencePeriodAdjustmentForm = ({ formik }: ReferencePeriodAdjustmentForm) => {
  const { t } = useTranslation()

  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const { companyPgId: companyId } = useLocationsContext()
  const { data: earliestConsumption } = useLoadPlanningEarliestConsumption({
    companyId: companyId!,
    energyType: mapToEnergyTypeDto(strategyMetadata?.energyType),
    year: strategyMetadata?.year,
  })

  const { minSelectableDate, maxSelectableDate, disabledYears, filterDate } = useMemo(
    () => getReferencePeriodPickerConfigFromEarliestConsumption(earliestConsumption),
    [earliestConsumption]
  )

  const selectedDates = [formik.values.referencePeriodStart, formik.values.referencePeriodEnd].filter((d): d is string => !!d)
  const excludedDates = selectedDates.length === 1 ? enforceAtLeast7DaysPicked(selectedDates[0]) : []

  const handleDateRangeUpdate = (dates: [string, string]) => {
    const initialDate = dates[0] ? dates[0] : ''
    const finalDate = dates[1] ? dates[1] : ''
    formik.setFieldValue('referencePeriodStart', initialDate)
    formik.setFieldValue('referencePeriodEnd', finalDate)
  }
  return (
    <FlexCol gap='16px' width='100%'>
      <FormDatePicker
        pickerType={'day'}
        isRange={true}
        name='dateRange'
        label={t('components.procurement.forecasting.modal.representative_alternative_period_label')}
        infoTooltip={t('components.procurement.forecasting.modal.representative_alternative_period_tooltip')}
        value={[formik.values.referencePeriodStart, formik.values.referencePeriodEnd]}
        onChange={dates => {
          const initialDate = dates[0] ? dates[0].toISOString() : ''
          const finalDate = dates[1] ? dates[1].toISOString() : ''
          handleDateRangeUpdate([initialDate, finalDate])
        }}
        excludeDates={excludedDates}
        isRequired={true}
        minDate={minSelectableDate}
        maxDate={maxSelectableDate}
        disabledYears={disabledYears}
        filterDate={filterDate}
        labelProps={{
          fontWeight: 86,
          fontSize: '14px',
          color: appColors.secondaryForeground,
        }}
      />
      <FlexCol gap='4px'>
        <FormLabel fontWeight={86} fontSize={'base'} color={appColors.secondaryForeground} marginBottom={'4px'}>
          {t('components.procurement.forecasting.modal.scaling_reference_period')}
        </FormLabel>
        <Box width={'80px'}>
          <FormNumericInput
            name='adjustmentValue'
            value={formik.values.percentageAdjustmentValue}
            onChange={val => formik.setFieldValue('percentageAdjustmentValue', val)}
            labelRight='%'
            placeholder='0'
            textAlign={'left'}
          />
        </Box>
      </FlexCol>
    </FlexCol>
  )
}
